import * as z from "zod";
import { disputeAlertType } from "./dispute-alert-definition";

/**
 * Alert dashboard analytics shapes, mirroring `dispute-analytics.ts`. The
 * `*ByCurrency` rows are the repo SELECT (amounts in integer MINOR units).
 */

// Bucket key is COALESCE(refunded_currency, dispute_currency): refunds-deployed
// is the only money metric, in the refund's currency; counts sum across buckets.
export const alertStatsByCurrencySchema = z.object({
  currency: z.string(),
  totalCount: z.coerce.number(),
  needsReviewCount: z.coerce.number(),
  resolvedCount: z.coerce.number(),
  cancelledCount: z.coerce.number(),
  autoResolvedCount: z.coerce.number(),
  manualReviewCount: z.coerce.number(),
  alreadyRefundedCount: z.coerce.number(),
  onTimeCount: z.coerce.number(),
  lateCount: z.coerce.number(),
  overdueCount: z.coerce.number(),
  rdrCount: z.coerce.number(),
  cdrnCount: z.coerce.number(),
  ethocaCount: z.coerce.number(),
  refundsDeployedCount: z.coerce.number(),
  refundsDeployedAmountMinor: z.coerce.number(),
});

export interface AlertStatsByCurrency extends z.infer<
  typeof alertStatsByCurrencySchema
> {}

/** One row per `automation_error` value (manual-review reasons). */
export const alertReviewReasonSchema = z.object({
  reason: z.string(),
  count: z.coerce.number(),
});

export interface AlertReviewReason extends z.infer<
  typeof alertReviewReasonSchema
> {}

/** Per (month, currency) alert volume + refunds, bucketed on `created_at`. */
export const monthlyAlertStatsByCurrencySchema = z.object({
  month: z.coerce.date(),
  currency: z.string(),
  totalCount: z.coerce.number(),
  rdrCount: z.coerce.number(),
  cdrnCount: z.coerce.number(),
  ethocaCount: z.coerce.number(),
  resolvedCount: z.coerce.number(),
  refundsDeployedAmountMinor: z.coerce.number(),
});

export interface MonthlyAlertStatsByCurrency extends z.infer<
  typeof monthlyAlertStatsByCurrencySchema
> {}

/** USD-merged monthly row returned to the client. */
export const monthlyAlertStatsSchema = z.object({
  month: z.string(),
  totalCount: z.coerce.number(),
  rdrCount: z.coerce.number(),
  cdrnCount: z.coerce.number(),
  ethocaCount: z.coerce.number(),
  resolvedCount: z.coerce.number(),
  refundsDeployedAmount: z.coerce.number(),
});

export type MonthlyAlertStats = z.infer<typeof monthlyAlertStatsSchema>;

export const alertStatsSchema = z.object({
  totalCount: z.coerce.number(),
  needsReviewCount: z.coerce.number(),
  resolvedCount: z.coerce.number(),
  cancelledCount: z.coerce.number(),
  autoResolvedCount: z.coerce.number(),
  manualReviewCount: z.coerce.number(),
  alreadyRefundedCount: z.coerce.number(),
  onTimeCount: z.coerce.number(),
  lateCount: z.coerce.number(),
  overdueCount: z.coerce.number(),
  rdrCount: z.coerce.number(),
  cdrnCount: z.coerce.number(),
  ethocaCount: z.coerce.number(),
  refundsDeployedCount: z.coerce.number(),
  refundsDeployedAmount: z.coerce.number(),
  currency: z.string(),
  reviewReasons: z.array(alertReviewReasonSchema).nullish(),
  monthly: z.array(monthlyAlertStatsSchema).nullish(),
  errors: z.array(z.string()).nullish(),
  priorPeriod: z
    .object({
      totalCount: z.coerce.number(),
      resolvedCount: z.coerce.number(),
      autoResolvedCount: z.coerce.number(),
      onTimeCount: z.coerce.number(),
      lateCount: z.coerce.number(),
      refundsDeployedCount: z.coerce.number(),
      refundsDeployedAmount: z.coerce.number(),
    })
    .nullish(),
});

export type AlertStats = z.infer<typeof alertStatsSchema>;

export const alertNetworks = disputeAlertType;
